import React from 'react';
import { Button, List, Tag, Typography, Divider, Space } from 'antd';
import { DeleteOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { toast } from 'react-toastify';

const { Text } = Typography;

interface CartPopoverProps {
  onClose: () => void;
}

const CartPopover: React.FC<CartPopoverProps> = ({ onClose }) => {
  const { items, removeItem, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const handleRemoveItem = (itemId: number, itemName: string) => {
    removeItem(itemId);
    toast.success(`${itemName} removed from cart`);
  };

  const handleCheckout = () => {
    onClose();
    navigate('/user/checkout');
  };

  if (items.length === 0) {
    return (
      <div style={emptyCartContainer}>
        <ShoppingOutlined style={emptyCartIcon} />
        <Text type="secondary">Your cart is empty</Text>
        <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
          Add some assessments to get started
        </Text>
      </div>
    );
  }

  return (
    <div style={cartContainer}>
      <List
        size="small"
        dataSource={items}
        renderItem={(item) => (
          <List.Item style={listItemStyle}>
            <div style={itemContentStyle}>
              <div style={itemHeaderStyle}>
                <Text strong style={itemNameStyle}>{item.name}</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveItem(item.id, item.name)}
                  style={removeButtonStyle}
                />
              </div>
              <div style={itemDetailsStyle}>
                <Tag
                  style={{
                    background: "#F4F8FF",
                    color: "#8E99BA",
                    border: "none",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 500,
                    padding: "0px 15px",
                    margin: 0,
                    lineHeight: "1.2rem",
                    marginRight: '8px',
                  }}
                >
                  {item.industry_name}
                </Tag>
                <Text strong style={priceStyle}>${item.price}</Text>
              </div>
            </div>
          </List.Item>
        )}
      />

      <Divider style={{ margin: '12px 0' }} />

      <div style={footerStyle}>
        <div style={totalStyle}>
          <Text strong>Total: ${(totalPrice || 0).toFixed(2)}</Text>
        </div>
        <Space style={{ width: '100%', marginTop: '8px', justifyContent: "right" }}>
          <Button onClick={() => { clearCart(); onClose(); }}>
            Clear Cart
          </Button>
          <Button type="primary" onClick={handleCheckout} style={checkoutButtonStyle}>
            Go to Checkout
          </Button>
        </Space>
      </div>
    </div>
  );
};

const cartContainer: React.CSSProperties = {
  width: '320px',
  maxHeight: '400px',
  overflow: 'auto',
};

const emptyCartContainer: React.CSSProperties = {
  width: '200px',
  textAlign: 'center',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const emptyCartIcon: React.CSSProperties = {
  fontSize: '32px',
  color: '#d9d9d9',
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  padding: '8px 0',
  border: 'none',
};

const itemContentStyle: React.CSSProperties = {
  width: '100%',
};

const itemHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '4px',
};

const itemNameStyle: React.CSSProperties = {
  fontSize: '14px',
  flex: 1,
  marginRight: '8px',
};

const removeButtonStyle: React.CSSProperties = {
  color: '#ff4d4f',
  padding: '0 4px',
};

const itemDetailsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const priceStyle: React.CSSProperties = {
  color: '#0050FF',
  fontSize: '14px',
};

const footerStyle: React.CSSProperties = {
  padding: '8px 0',
};

const totalStyle: React.CSSProperties = {
  textAlign: 'right',
  fontSize: '16px',
};

const checkoutButtonStyle: React.CSSProperties = {
  background: 'rgb(29, 31, 72)',
  flex: 1,
};

export default CartPopover;